import Boot from './app/states/Boot';
import Load from './app/states/Load';
import Welcome from './app/states/Welcome';
import Game from './app/states/Game';
import CharacterIntro from 'app/states/CharacterIntro';
import LevelEnd from './app/states/LevelEnd';
import LevelLose from './app/states/LevelLose';
import GameEnd from './app/states/GameEnd';
import CutScene from './app/states/CutScene';

import GameController from './app/controller/GameController';
import AppUrls from './app/constants/AppUrls';
import SoundController from 'app/controller/SoundController';
import CopyController from 'app/controller/CopyController';
import AspectController from 'app/controller/AspectController';
import WindowEventController from 'app/controller/WindowEventController';
import Hud from 'app/ui/Hud';
import {
  GAME_HEIGHT_DEFAULT,
  GAME_WIDTH_DEFAULT,
  STATE_BOOT,
  STATE_LOAD,
  STATE_WELCOME,
  STATE_GAME,
  STATE_CHARACTER_INTRO,
  STATE_LEVELEND,
  STATE_LEVELLOSE,
  STATE_GAMEEND,
  STATE_CUTSCENE,
  Y_PAD_SCALE,
  X_PAD_SCALE,
  GAME_DIV_CLASSNAME,
  GAME_DIV_ID,
  WISETRACK_ID,
  WISETRACK_COUNTRY,
} from 'app/constants/AppConstants';
import { prettyLog } from 'utils/prettyLog';
import * as AppTrackingEvents from './app/constants/AppTrackingEvents';

class App extends Phaser.Game {
  constructor({ width, height, type: renderer, parent = GAME_DIV_ID, settings, deviceController }) {
    //renderer = 1;
    super(width, height, renderer, parent, null, true);

    const gameContainer = document.getElementById(parent);
    if (gameContainer) {
      gameContainer.className = GAME_DIV_CLASSNAME;
    }

    this.defaultWidth = GAME_WIDTH_DEFAULT;
    this.defaultHeight = GAME_HEIGHT_DEFAULT;
    this.offsetWidth = 0;
    this.offsetHeight = 0;
    this._screenPaddingY = Math.max(this.width, this.height) * Y_PAD_SCALE;
    this._screenPaddingX = Math.max(this.width, this.height) * X_PAD_SCALE;

    this._hud = new Hud(this);

    this._controller = new GameController({ game: this, settings, hud: this._hud, deviceController });
    this._sounds = new SoundController({ game: this, isMuted: settings.getIsMuted() });

    this._aspectController = new AspectController({ game: this });
    this._windowEventController = new WindowEventController({ game: this, activate: true });
    this._urls = new AppUrls(this);
    this._settings = settings;
    this._deviceController = deviceController;

    this.state.add(STATE_BOOT, Boot, false);
    this.state.add(STATE_LOAD, Load, false);
    this.state.add(STATE_WELCOME, Welcome, false);
    this.state.add(STATE_GAME, Game, false);
    this.state.add(STATE_CHARACTER_INTRO, CharacterIntro, false);
    this.state.add(STATE_LEVELEND, LevelEnd, false);
    this.state.add(STATE_LEVELLOSE, LevelLose, false);
    this.state.add(STATE_GAMEEND, GameEnd, false);
    this.state.add(STATE_CUTSCENE, CutScene, false);

    const rendererLabel = ['AUTO', 'CANVAS', 'WEBGL', 'WEBGL_MULTI', 'HEADLESS'][this.renderType] || 'UNKNOWN';
    const webpackInfo = [
      { key: 'DEBUG:', val: __DEBUG__ },
      { key: 'WATCH:', val: __WATCH__ },
      { key: 'SHORTCUTS:', val: __SHORTCUTS_ENABLED__ },
      { key: 'Node Env:', val: process.env.NODE_ENV },
    ];
    const deviceInfo = [
      { key: 'Is old:', val: deviceController.requiresLightVersion },
      { key: 'Transitions:', val: deviceController.allowTransitionTweens },
      { key: 'Load type:', val: deviceController.gameLoadType },
      { key: 'Renderer:', val: rendererLabel },
    ];
    prettyLog([{ label: 'Webpack', output: webpackInfo }, { label: 'Device', output: deviceInfo }]);
    this.controller.startBoot();

    //**********/
    //WISETRACK /
    //**********/

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = '//www.wisetrack.net/js2_detect.php?tracking_id=' + WISETRACK_ID + '&country=' + WISETRACK_COUNTRY;
    document.getElementsByTagName('head')[0].appendChild(script);

    script.onload = this.wisetrackLoadedHandler.bind(this);

    //
    window.addEventListener('blur', this.handleWindowBlur.bind(this));
    window.addEventListener('focus', this.handleWindowFocus.bind(this));
  }

  wisetrackLoadedHandler() {
    let iframe = parent !== window;
    let url = null;
    if (iframe) {
      url = document.referrer;
      // console.log('URL : ' + url);
      // console.log('REFERER : ' + this.extractHostName(url));
      let event = AppTrackingEvents.getReferrerString(url);
      console.log(event);
      // this.track();
    }
  }

  extractHostName(url) {
    let hostname;

    if (url.indexOf('//') > -1) {
      hostname = url.split('/')[2];
    } else {
      hostname = url.split('/')[0];
    }

    hostname = hostname.split(':')[0];
    hostname = hostname.split('?')[0];

    return hostname;
  }

  extractRootDomain(url) {}

  /*
  ====================================================================================
    GETTERS/SETTERS
  ====================================================================================
  */

  handleWindowBlur() {
    if (this.state.current != STATE_GAME) {
      this.focusLost = true;
      this.paused = true;
    } else {
      this.sounds.setGameMusicVolume(0);
    }
  }

  handleWindowFocus() {
    if (this.state.current != STATE_GAME) {
      this.focusLost = false;
      this.paused = false;
    } else {
      this.sounds.setGameMusicVolume(1);
    }
  }

  /*
  ====================================================================================
    GETTERS/SETTERS
  ====================================================================================
  */
  get settings() {
    return this._settings;
  }

  get deviceController() {
    return this._deviceController;
  }

  get hud() {
    return this._hud;
  }

  get copy() {
    return this._copy;
  }
  addCopy(xml) {
    this._copy = new CopyController(xml);
  }

  get controller() {
    return this._controller;
  }

  get sounds() {
    return this._sounds;
  }

  get aspectController() {
    return this._aspectController;
  }

  get windowEventController() {
    return this._windowEventController;
  }

  get urls() {
    return this._urls;
  }

  set screenPaddingY(val) {
    this._screenPaddingY = val;
  }
  get screenPaddingY() {
    return this._screenPaddingY;
  }
  set screenPaddingX(val) {
    this._screenPaddingX = val;
  }
  get screenPaddingX() {
    return this._screenPaddingX;
  }

  track(evt) {
    if (window.WiseTrack) {
      window.WiseTrack.track(evt);
    }
  }

  log(message, ...args) {
    if (__DEBUG__) {
      const styl = 'color: orange; font-weight: bold';

      if (message.indexOf('Starting Game') !== -1) {
        console.log(`\n%c${message}`, styl, '\n ');
      } else {
        if (args) {
          let a = [];
          args.forEach((arg) => {
            a.push(arg);
            a.push('\n');
          });
          console.log(`\n%c${message}`, styl, '\n\n', ...a, '\n ');
          // console.log('\n' + message + '\n\n  ', ...args, '\n ');
        } else {
          console.log(`\n%c${message}`, styl, '\n ');
        }
      }
    }
  }
}

export default App;
